import { StandardGroupingGrid, StandardGroupingGridPresentationType } from '@apply/components';
import { Column, MasterDetail, Summary, TotalItem } from 'devextreme-react/data-grid';
import { CellPreparedEvent, CustomSummaryInfo } from 'devextreme/ui/data_grid';
import { IBridgeLoanGridRecord } from '@apply/api/src/api/loanApi';
import dayjs from 'dayjs';
import { formatTerm } from '../../../common/functions/formatters/formatTerm';
import { experienceRender } from '../../../common/functions/renderers/experienceRender';
import { fullTextRender } from '../../../common/functions/renderers/fullTextRender';
import { initialsRender } from '../../../common/functions/renderers/initialsRender';
import { RepaidLoanActionsButton } from './actions/RepaidLoanActionsButton';
import { RepaidPortfolioPropertiesGrid } from './components/RepaidPortfolioPropertiesGrid';
import { repaidLoanFormats as formats } from './functions/formatters/repaidLoanFormats';
import { defaultStatusBarRender } from './functions/renderers/defaultStatusBarRender';
import { repaidLoanSummaryItems, underwrittenLtvAverageHint } from './functions/summaries/repaidLoanSummaries';

export interface RepaidLoansGridProps {
    data: IBridgeLoanGridRecord[] | undefined;
}

const highlightColor = '#1F4FD1';

const summaryItemsByName = new Map(repaidLoanSummaryItems.map((item) => [item.name, item]));

// Each footer item collects its rows, then summarizes them all at once
const calculateCustomSummary = (options: CustomSummaryInfo) => {
    const item = summaryItemsByName.get(options.name ?? '');
    if (!item) return;

    switch (options.summaryProcess) {
        case 'start':
            options.totalValue = [];
            break;
        case 'calculate':
            options.totalValue.push(options.value); // The whole row, since the items have no `column`
            break;
        case 'finalize':
            options.totalValue = item.text(options.totalValue);
            break;
    }
};

const onCellPrepared = (e: CellPreparedEvent<unknown, unknown>) => {
    if (e.rowType !== 'totalFooter') return;

    const underwrittenLtvAverage = e.cellElement.querySelector<HTMLElement>('.underwrittenLtvAverage');
    if (underwrittenLtvAverage) {
        underwrittenLtvAverage.title = underwrittenLtvAverageHint;
        underwrittenLtvAverage.style.setProperty('color', highlightColor, 'important');
    }
};

const formatDate = (date: string | null) => (date ? dayjs(date).format('M/D/YY') : '');

// The repaid date, with both the repaid and closed dates as a tooltip
const repaidDateRender = (loan: IBridgeLoanGridRecord) => (
    <span
        title={`Repaid: ${formatDate(loan.repaidDate)}\nClosed: ${formatDate(loan.closingDate)}`}
        style={{ color: highlightColor }}
    >
        {formatDate(loan.repaidDate)}
    </span>
);

// Ungrouped, unlike the Outstanding grid; rows keep the server's order (most recently repaid first)
export const RepaidLoansGrid = ({ data }: RepaidLoansGridProps) => (
    <StandardGroupingGrid
        dataSource={data}
        keyExpr='id'
        presentationType={StandardGroupingGridPresentationType.Enhanced}
        noDataText='No Repaid Loans Found'
        pagerVisible={false}
        groupPanelVisible={false}
        rowAlternationEnabled={true}
        showColumnHeaders={true}
        showBorders={true}
        remoteOperations={false}
        allowColumnReordering={false}
        masterDetailTrigger='isPortfolio'
        defaultPageSize={0}
        onCellPrepared={onCellPrepared}
    >
        <Column
            dataField='bfiNumber'
            caption='Loan'
            width={70}
            alignment='left'
        />
        <Column
            name='defaultStatus'
            width={20}
            allowSorting={false}
            allowResizing={false}
            cellRender={(e) => defaultStatusBarRender(e.data)}
        />
        <Column
            dataField='borrower'
            caption='Borrower'
            width={150}
            alignment='left'
        />
        <Column
            dataField='city'
            caption='City'
            width={110}
            alignment='left'
        />
        <Column
            dataField='state'
            caption='State'
            width={45}
            alignment='left'
        />
        <Column
            dataField='units'
            caption='Units'
            width={45}
            alignment='left'
        />
        <Column
            dataField='size'
            caption='Size'
            width={95}
            alignment='left'
            customizeText={(e) => formats.currency(e.value)}
        />
        <Column
            dataField='oneYearTreasuryRate'
            caption='1YT'
            width={55}
            alignment='left'
            customizeText={(e) => formats.oneYearTreasuryRate(e.value)}
        />
        <Column
            dataField='estimatedRate'
            caption='Rate'
            width={55}
            alignment='left'
            customizeText={(e) => formats.rate(e.value)}
        />
        <Column
            dataField='adjustmentRate'
            caption='LLA'
            width={50}
            alignment='left'
            customizeText={(e) => formats.adjustmentRate(e.value)}
        />
        <Column
            dataField='servicingRate'
            caption='Serv.'
            width={50}
            alignment='left'
            customizeText={(e) => formats.servicingRate(e.value)}
        />
        <Column
            name='term'
            caption='Term'
            width={60}
            alignment='left'
            calculateCellValue={(loan: IBridgeLoanGridRecord) =>
                formatTerm(loan.initialTermMonths, loan.contractualExtension1Months, loan.contractualExtension2Months)
            }
        />
        <Column
            dataField='underwrittenLtv'
            caption='LTVuw'
            width={55}
            alignment='left'
            customizeText={(e) => formats.ltv(e.value)}
        />
        <Column
            dataField='appraisedLtv'
            caption='LTVa'
            width={50}
            alignment='left'
            customizeText={(e) => formats.ltv(e.value)}
        />
        <Column
            dataField='appraisedValue'
            caption='Appraised'
            width={95}
            alignment='left'
            customizeText={(e) => formats.currency(e.value)}
        />
        <Column
            dataField='cashOut'
            caption='CO'
            width={90}
            alignment='left'
            customizeText={(e) => formats.currency(e.value)}
        />
        <Column
            dataField='creditScoreRate'
            caption='Credit'
            width={50}
            alignment='left'
            customizeText={(e) => formats.credit(e.value)}
        />
        <Column
            dataField='experience'
            caption='Experience'
            width={70}
            alignment='left'
            cellRender={(e) => experienceRender(e)}
        />
        <Column
            dataField='refinanceLender'
            caption='BFI Refi'
            width={60}
            alignment='left'
            cellRender={(e) => fullTextRender(e.value)}
        />
        <Column
            dataField='broker'
            caption='Broker'
            width={100}
            alignment='left'
        />
        <Column
            dataField='repaidDate'
            caption='Repaid'
            width={70}
            alignment='left'
            dataType='date'
            cellRender={(e) => repaidDateRender(e.data)}
        />
        <Column
            dataField='exception'
            caption='Exception'
            width={150}
            alignment='left'
            cellRender={(e) => fullTextRender(e.value)}
        />
        <Column
            dataField='primaryMotivation'
            caption='Primary Motivation'
            width={170}
            alignment='left'
            cellRender={(e) => fullTextRender(e.value)}
        />
        <Column
            caption='POC'
            width={45}
            alignment='left'
            cellRender={(e) => initialsRender(e.data.pointContactFirstName, e.data.pointContactLastName)}
        />
        <Column
            alignment='center'
            cellRender={(e) => <RepaidLoanActionsButton loan={e.data} />}
            width={40}
            allowReordering={false}
            allowSorting={false}
            allowResizing={false}
            type='buttons'
            fixed={true}
            fixedPosition={'right'}
        />
        <MasterDetail
            enabled={true}
            render={(e) => <RepaidPortfolioPropertiesGrid loan={e.data} />}
        />
        <Summary calculateCustomSummary={calculateCustomSummary}>
            {repaidLoanSummaryItems.map((item) => (
                <TotalItem
                    key={item.name}
                    name={item.name}
                    showInColumn={item.column}
                    summaryType='custom'
                    displayFormat='{0}'
                    customizeText={(e) => e.valueText || '\u00A0'} // A non-breaking space, so a blank still takes up its row
                    cssClass={item.cssClass}
                />
            ))}
        </Summary>
    </StandardGroupingGrid>
);
